import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Building2, Plus, Users, Crown, MessageSquare, Cpu, Mail, UserPlus, Settings2, Clock, CheckCircle, XCircle, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";

const ROLE_LABELS: Record<string, string> = { owner: "Dono", admin: "Admin", member: "Membro", viewer: "Visualizador" };
const ROLE_COLORS: Record<string, string> = {
  owner: "bg-warning/15 text-warning border-warning/30",
  admin: "bg-primary/15 text-primary border-primary/30",
  member: "bg-muted text-foreground border-border",
  viewer: "bg-muted text-muted-foreground border-border",
};

interface OrgDetailProps {
  orgId: string;
  orgName: string;
  onClose: () => void;
}

function OrgDetail({ orgId, orgName, onClose }: OrgDetailProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");

  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: ["org-detail-members", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("org_members")
        .select("*, profiles(name, email)")
        .eq("org_id", orgId);
      if (error) throw error;
      return data as any[];
    },
  });

  const { data: invites = [] } = useQuery({
    queryKey: ["org-invites", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("org_invites")
        .select("*")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const inviteMut = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("org-management", {
        body: { action: "invite", org_id: orgId, email: inviteEmail, role: inviteRole },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org-invites", orgId] });
      setInviteEmail("");
      toast({ title: "Convite enviado!" });
    },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const revokeMut = useMutation({
    mutationFn: async (email: string) => {
      const { data, error } = await supabase.functions.invoke("org-management", {
        body: { action: "revoke", org_id: orgId, email },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org-invites", orgId] });
      toast({ title: "Convite revogado" });
    },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const updateRoleMut = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: string }) => {
      const { data, error } = await supabase.functions.invoke("org-management", {
        body: { action: "update_role", org_id: orgId, email, role },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org-detail-members", orgId] });
      toast({ title: "Papel atualizado" });
    },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const pendingInvites = invites.filter(i => i.status === "pending");

  return (
    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Building2 className="w-5 h-5" /> {orgName}
        </DialogTitle>
      </DialogHeader>

      <Tabs defaultValue="members" className="mt-2">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="members">
            <Users className="w-4 h-4 mr-1" /> Membros ({members.length})
          </TabsTrigger>
          <TabsTrigger value="invites">
            <Mail className="w-4 h-4 mr-1" /> Convites ({pendingInvites.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-4 space-y-3">
          {membersLoading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div>
          ) : members.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Nenhum membro.</p>
          ) : (
            members.map((m: any) => (
              <div key={m.id} className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <p className="text-sm font-medium">{m.profiles?.name || "Sem nome"}</p>
                  <p className="text-xs text-muted-foreground">{m.profiles?.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={m.role || "member"}
                    onValueChange={(newRole) => {
                      if (m.profiles?.email) {
                        updateRoleMut.mutate({ email: m.profiles.email, role: newRole });
                      }
                    }}
                  >
                    <SelectTrigger className="w-32 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">Dono</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="member">Membro</SelectItem>
                      <SelectItem value="viewer">Visualizador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))
          )}
        </TabsContent>

        <TabsContent value="invites" className="mt-4 space-y-4">
          {/* Invite form */}
          <form
            onSubmit={(e) => { e.preventDefault(); if (inviteEmail.trim()) inviteMut.mutate(); }}
            className="flex items-end gap-2"
          >
            <div className="flex-1 space-y-1">
              <label className="text-xs font-medium">Email</label>
              <Input
                type="email"
                placeholder="usuario@email.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                className="h-9"
              />
            </div>
            <div className="w-32 space-y-1">
              <label className="text-xs font-medium">Papel</label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="member">Membro</SelectItem>
                  <SelectItem value="viewer">Visualizador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" size="sm" disabled={inviteMut.isPending} className="h-9">
              <UserPlus className="w-4 h-4 mr-1" /> Convidar
            </Button>
          </form>

          {/* Pending invites */}
          {pendingInvites.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Nenhum convite pendente.</p>
          ) : (
            <div className="space-y-2">
              {pendingInvites.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg border">
                  <div>
                    <p className="text-sm font-medium">{inv.email}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="outline" className={`text-xs ${ROLE_COLORS[inv.role] || ""}`}>
                        {ROLE_LABELS[inv.role] || inv.role}
                      </Badge>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Expira {format(new Date(inv.expires_at), "dd/MM", { locale: ptBR })}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive"
                    onClick={() => revokeMut.mutate(inv.email)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </DialogContent>
  );
}

export default function Organizations() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [selectedOrg, setSelectedOrg] = useState<{ id: string; name: string } | null>(null);

  const { data: orgs = [], isLoading } = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, owner_id, plan, monthly_message_limit, monthly_ai_calls_limit, created_at, updated_at")
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const { data: orgMembers = [] } = useQuery({
    queryKey: ["org-members"],
    queryFn: async () => {
      const { data, error } = await supabase.from("org_members").select("*");
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const createMut = useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await supabase.from("organizations").insert({ name } as any).select().single();
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["organizations"] }); setShowCreate(false); setNewName(""); toast({ title: "Organização criada" }); },
    onError: (err: Error) => toast({ title: "Erro", description: err.message, variant: "destructive" }),
  });

  const memberCountMap = orgMembers.reduce((acc: Record<string, number>, m) => {
    acc[m.org_id] = (acc[m.org_id] || 0) + 1;
    return acc;
  }, {});

  const planColors: Record<string, string> = {
    free: "bg-muted text-muted-foreground border-border",
    starter: "bg-info/15 text-info border-info/30",
    pro: "bg-primary/15 text-primary border-primary/30",
    business: "bg-success/15 text-success border-success/30",
    enterprise: "bg-warning/15 text-warning border-warning/30",
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground font-headline tracking-tight">Organizações</h1>
          <p className="text-muted-foreground mt-1">Gerencie organizações, membros e convites</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="shadow-sm">
          <Plus className="w-4 h-4 mr-1" /> Nova Organização
        </Button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-52 rounded-lg" />)}
        </div>
      ) : orgs.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Building2 className="w-12 h-12 mb-3 opacity-30" />
          <p className="text-sm">Nenhuma organização cadastrada.</p>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {orgs.map((org, index) => {
            const plan = org.plan || "free";
            return (
              <motion.div
                key={org.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <Card
                  className="relative overflow-hidden border-border/50 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer"
                  onClick={() => setSelectedOrg({ id: org.id, name: org.name })}
                >
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary" />
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                        <Building2 className="w-5 h-5" />
                      </div>
                      <div>
                        <CardTitle className="text-base font-semibold">{org.name}</CardTitle>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                          <Crown className="w-3 h-3" />
                          {org.owner_id ? "Com dono" : "Sem dono"}
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline" className={`text-xs ${planColors[plan] || planColors.free}`}>
                      {plan}
                    </Badge>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="w-4 h-4" />
                      <span className="font-medium text-foreground">{memberCountMap[org.id] || 0}</span> membros
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50">
                        <MessageSquare className="w-4 h-4 text-info" />
                        <div>
                          <p className="text-xs text-muted-foreground">Msgs/mês</p>
                          <p className="text-sm font-semibold text-foreground">{org.monthly_message_limit || 1000}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50">
                        <Cpu className="w-4 h-4 text-warning" />
                        <div>
                          <p className="text-xs text-muted-foreground">IA/mês</p>
                          <p className="text-sm font-semibold text-foreground">{org.monthly_ai_calls_limit || 500}</p>
                        </div>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="w-full" onClick={(e) => { e.stopPropagation(); setSelectedOrg({ id: org.id, name: org.name }); }}>
                      <Settings2 className="w-4 h-4 mr-1" /> Gerenciar
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nova Organização</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); if (newName.trim()) createMut.mutate(newName.trim()); }} className="space-y-4">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nome da organização" required />
            <Button type="submit" disabled={createMut.isPending} className="w-full">
              {createMut.isPending ? "Criando..." : "Criar"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Org Detail Dialog */}
      <Dialog open={!!selectedOrg} onOpenChange={(open) => { if (!open) setSelectedOrg(null); }}>
        {selectedOrg && (
          <OrgDetail orgId={selectedOrg.id} orgName={selectedOrg.name} onClose={() => setSelectedOrg(null)} />
        )}
      </Dialog>
    </div>
  );
}
